/**
 * What the signed-in account's plan allows, for showing locks and upgrade prompts before the
 * server says no. The rules are the server's (packages/core/src/plans.ts); FMS staff see
 * everything.
 */
import { createContext, useContext, type ReactNode } from "react";
import type { Entitlement } from "./api.ts";
import { FEATURE_TEXT, PLAN_INFO, planFor, type Feature } from "../../../packages/core/src/plans.ts";
import { go } from "./route.ts";
import { Icon } from "./ui.tsx";

const Ctx = createContext<Entitlement | null>(null);

export function PlanProvider({ plan, children }: { plan: Entitlement | null; children: ReactNode }) {
  return <Ctx.Provider value={plan}>{children}</Ctx.Provider>;
}

/** True when this account may use the feature (always for FMS staff). */
export function useCan(feature: Feature): boolean {
  const e = useContext(Ctx);
  return !e || e.features.includes(feature);
}

export const usePlan = () => useContext(Ctx);

/** Shown in place of a feature the plan doesn't include. */
export function LockCard({ feature, children }: { feature: Feature; children?: ReactNode }) {
  const t = FEATURE_TEXT[feature];
  const plan = PLAN_INFO[planFor(feature)];
  return (
    <div className="card lock">
      {children ? <div className="lock-preview" aria-hidden="true">{children}</div> : <div className="lock-preview lock-blank" aria-hidden="true" />}
      <div className="lock-body">
        <span className="pro-badge">{plan.name.toUpperCase()}</span>
        <h3>{t.title}</h3>
        <p>{t.body}</p>
        <div className="toolbar">
          <button className="btn primary" onClick={() => go({ name: "admin", tab: "plan" })}>
            <Icon name="spark" size={16} /> See plans
          </button>
        </div>
      </div>
    </div>
  );
}

/** A line at the top of the app while a trial is on, with days left. */
export function TrialBanner() {
  const e = useContext(Ctx);
  if (!e?.trialDaysLeft) return null;
  return (
    <div className="banner info small trial-banner">
      <b>Farm Pro trial:</b> {e.trialDaysLeft} day{e.trialDaysLeft === 1 ? "" : "s"} left.{" "}
      <button className="linkish" onClick={() => go({ name: "admin", tab: "plan" })}>
        Keep Farm Pro
      </button>
    </div>
  );
}
